import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faMobileAlt,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBuilding,
    faMoneyBillWave,
    faWallet,
    faUniversity,
    faCreditCard,
    faLandmark,
    faChartLine,
    faUsers,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faClock,
    faFileInvoice,
    faReceipt,
    faMoneyBill,
    faInfoCircle,
    faExchangeAlt,
    faArrowUp,
    faArrowDown,
    faBalanceScale,
    faPercent,
    faDollarSign,
    faStore,
    faHistory
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance for partner transactions
const createPartnerTransactionsTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_partner_transactions_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Partner Transactions");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_partner_transactions_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        // Main headers
        partnerTransactions: 'Partner Transactions',
        allTransactions: 'All Transactions',
        transactions: 'Transactions',

        // Shop info
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',

        // Search and actions
        searchPlaceholder: 'Search transactions...',
        refresh: 'Refresh',
        loadingTransactions: 'Loading transactions...',
        noTransactionsFound: 'No transactions found',

        // Modal texts
        transactionDetails: 'Transaction Details',
        close: 'Close',

        // Table headers
        serialNo: 'S.No',
        id: 'ID',
        transactionDate: 'Transaction Date',
        partnerName: 'Partner Name',
        transactionType: 'Type',
        paymentMethod: 'Payment Method',
        amount: 'Amount',
        balanceBefore: 'Balance Before',
        balanceAfter: 'Balance After',
        referenceNo: 'Reference No',
        notes: 'Notes',
        actions: 'Actions',
        addedBy: 'Added By',

        // Transaction types
        credit: 'Credit',
        debit: 'Debit',

        // Payment methods
        cash: 'Cash',
        cheque: 'Cheque',
        bank_transfer: 'Bank Transfer',
        digital_wallet: 'Digital Wallet',
        card: 'Card',
        upi: 'UPI',

        // Action buttons
        view: 'View',

        // Messages
        networkError: 'Network error occurred',

        // Modal details
        transactionId: 'Transaction ID',
        partnerDetails: 'Partner Details',
        transactionInfo: 'Transaction Information',
        notProvided: 'Not provided',

        // Pagination
        showing: 'Showing',
        of: 'of',
        items: 'items',
        previous: 'Previous',
        next: 'Next',

        // Filter texts
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        partnerNameFilter: 'Partner Name',
        transactionTypeFilter: 'Transaction Type',
        paymentMethodFilter: 'Payment Method',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        addedByFilter: 'Added By',
        minAmount: 'Min Amount',
        maxAmount: 'Max Amount',

        // Summary Stats
        totalTransactions: 'Total Transactions',
        totalCredits: 'Total Credits',
        totalDebits: 'Total Debits',
        netBalance: 'Net Balance',
        totalAmount: 'Total Amount',
        
        // PDF Export
        generatingPDF: 'Generating PDF...',
        pdfGenerationError: 'Error generating PDF',
        pdfGenerated: 'PDF generated successfully',
        reportFilters: 'Applied Filters',
        reportDate: 'Report Date',
        
        // Business Info
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        transactionReport: 'Transaction Report',
        transactionSummary: 'Transaction Summary',

        // Toast Messages
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        
        // Date formatting
        date: 'Date',
        
        // RTL support
        rtl: 'rtl',
        ltr: 'ltr'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const partnerTransactionsTranslator = createPartnerTransactionsTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Transaction Type Badge Component
const TransactionTypeBadge = ({ type, translations }) => {
    const getTypeStyle = (type) => {
        switch (type) {
            case 'credit':
                return { bg: '#10b98120', color: '#10b981', border: '#10b98140', icon: faArrowUp };
            case 'debit':
                return { bg: '#ef444420', color: '#ef4444', border: '#ef444440', icon: faArrowDown };
            default:
                return { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faExchangeAlt };
        }
    };

    const style = getTypeStyle(type);
    const label = translations?.[type] || (type === 'credit' ? 'Credit' : 'Debit');

    return (
        <div className="status-badge" style={{
            backgroundColor: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '4px 8px',
            borderRadius: '20px',
            fontSize: '12px'
        }}>
            <FontAwesomeIcon icon={style.icon} size="xs" />
            {label}
        </div>
    );
};

// Payment Method Badge Component
const PaymentMethodBadge = ({ method, translations }) => {
    const getMethodStyle = (method) => {
        const methodMap = {
            cash: { bg: '#10b98120', color: '#10b981', icon: faMoneyBillWave },
            cheque: { bg: '#8b5cf620', color: '#8b5cf6', icon: faUniversity },
            bank_transfer: { bg: '#3b82f620', color: '#3b82f6', icon: faLandmark },
            digital_wallet: { bg: '#f59e0b20', color: '#f59e0b', icon: faWallet },
            card: { bg: '#ec489a20', color: '#ec489a', icon: faCreditCard },
            upi: { bg: '#06b6d420', color: '#06b6d4', icon: faMobileAlt }
        };
        
        const style = methodMap[method] || { bg: '#6b728020', color: '#6b7280', icon: faMoneyBill };
        const label = translations?.[method] || method?.replace('_', ' ') || 'Cash';
        
        return (
            <div className="status-badge" style={{
                backgroundColor: style.bg,
                color: style.color,
                border: `1px solid ${style.border || style.bg}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 8px',
                borderRadius: '20px',
                fontSize: '12px'
            }}>
                <FontAwesomeIcon icon={style.icon} size="xs" />
                {label.charAt(0).toUpperCase() + label.slice(1)}
            </div>
        );
    };
    
    return getMethodStyle(method);
};

// Main Component
const PartnerTransactions = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#8b5cf6';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterBy, setFilterBy] = useState('all');
    const [partnerNameFilter, setPartnerNameFilter] = useState('');
    const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Transaction type options
    const transactionTypeOptions = [
        { value: '', label: 'All Types' },
        { value: 'credit', label: 'Credit' },
        { value: 'debit', label: 'Debit' }
    ];

    // Payment method options
    const paymentMethodOptions = [
        { value: '', label: 'All Methods' },
        { value: 'cash', label: 'Cash' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'digital_wallet', label: 'Digital Wallet' },
        { value: 'card', label: 'Card' },
        { value: 'upi', label: 'UPI' }
    ];

    // Calculate totals
    const totalTransactions = filteredTransactions.length;
    const totalCredits = filteredTransactions
        .filter(t => t.transaction_type === 'credit')
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    const totalDebits = filteredTransactions
        .filter(t => t.transaction_type === 'debit')
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    const netBalance = totalCredits - totalDebits;

    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await partnerTransactionsTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch transactions on mount and when shop changes
    useEffect(() => {
        if (user && user.username) {
            fetchTransactions();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [transactions, searchTerm, filterBy, partnerNameFilter, transactionTypeFilter, 
        paymentMethodFilter, addedByFilter, startDate, endDate, minAmount, maxAmount]);

    // Fetch available shops for switching
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchTransactions();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || '',
                bussinesslogo: '',
                businessaddress: '',
                fullname: user.fullname || '',
                email: user.email || ''
            });
        }
    };

    const fetchTransactions = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/customeraccount.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_partners_transactions',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setTransactions(data.data || []);
                resetFilters();

                if (data.data.length === 0) {
                    showToast('info', 'No partner transactions found in this shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...transactions];

        // Apply quick search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(transaction =>
                transaction.partner_name?.toLowerCase().includes(term) ||
                transaction.reference_no?.toLowerCase().includes(term) ||
                transaction.notes?.toLowerCase().includes(term) ||
                transaction.added_by_username?.toLowerCase().includes(term)
            );
        }

        // Apply advanced filters
        if (filterBy === 'partnerName' && partnerNameFilter.trim()) {
            const term = partnerNameFilter.toLowerCase();
            filtered = filtered.filter(transaction =>
                transaction.partner_name?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'transactionType' && transactionTypeFilter) {
            filtered = filtered.filter(transaction =>
                transaction.transaction_type === transactionTypeFilter
            );
        }

        if (filterBy === 'paymentMethod' && paymentMethodFilter) {
            filtered = filtered.filter(transaction =>
                transaction.payment_method === paymentMethodFilter
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(transaction =>
                transaction.added_by_username?.toLowerCase().includes(term)
            );
        }

        // Apply amount range filter
        if (minAmount) {
            filtered = filtered.filter(transaction =>
                parseFloat(transaction.amount) >= parseFloat(minAmount)
            );
        }

        if (maxAmount) {
            filtered = filtered.filter(transaction =>
                parseFloat(transaction.amount) <= parseFloat(maxAmount)
            );
        }

        // Apply date range filter
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(transaction => {
                if (!transaction.transaction_date) return false;
                const transDate = new Date(transaction.transaction_date);
                transDate.setHours(0, 0, 0, 0);
                return transDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(transaction => {
                if (!transaction.transaction_date) return false;
                const transDate = new Date(transaction.transaction_date);
                return transDate <= end;
            });
        }

        setFilteredTransactions(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setPartnerNameFilter('');
        setTransactionTypeFilter('');
        setPaymentMethodFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setMinAmount('');
        setMaxAmount('');
        setSearchTerm('');
    };

    const handleSearch = () => {
        applyFilters();
    };

    // PDF Export Function
    const exportToPDF = async () => {
        if (!filteredTransactions || filteredTransactions.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        setPdfLoading(true);
        showToast('info', translations.generatingPDF || 'Generating PDF...');

        try {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // Add business logo if available
            if (businessInfo.bussinesslogo) {
                try {
                    doc.addImage(businessInfo.bussinesslogo, 'PNG', 10, 5, 30, 15);
                } catch (e) {
                    console.log('Could not add logo:', e);
                }
            }

            // Set document properties
            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text(translations.transactionReport || 'Partner Transactions Report', 148, 15, { align: 'center' });

            doc.setFontSize(10);
            doc.text(businessInfo.businessname || user?.business_name || '', 148, 22, { align: 'center' });
            
            if (businessInfo.businessaddress) {
                doc.setFontSize(8);
                doc.text(businessInfo.businessaddress, 148, 27, { align: 'center' });
            }

            // Report metadata
            doc.setFontSize(8);
            const reportDate = new Date().toLocaleDateString();
            const generatedBy = `${translations.generatedBy || 'Generated by'}: ${user?.fullname || user?.username}`;
            doc.text(`${translations.reportDate || 'Report Date'}: ${reportDate} | ${generatedBy}`, 148, 32, { align: 'center' });

            // Shop info
            if (currentShop) {
                doc.text(`${translations.shopName || 'Shop'}: ${currentShop.shop_name} (${currentShop.shop_code || ''})`, 148, 37, { align: 'center' });
            }

            // Summary stats
            let yPos = 45;
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            
            // Create summary table
            autoTable(doc, {
                startY: yPos,
                head: [[
                    translations.totalTransactions || 'Total',
                    translations.totalCredits || 'Total Credits',
                    translations.totalDebits || 'Total Debits',
                    translations.netBalance || 'Net Balance'
                ]],
                body: [[
                    totalTransactions,
                    `$${totalCredits.toFixed(2)}`,
                    `$${totalDebits.toFixed(2)}`,
                    `$${netBalance.toFixed(2)}`
                ]],
                theme: 'grid',
                headStyles: {
                    fillColor: [139, 92, 246],
                    textColor: [255, 255, 255],
                    fontSize: 8,
                    halign: 'center'
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: 'center'
                },
                margin: { left: 10, right: 10 }
            });

            // Prepare table data
            const tableData = filteredTransactions.map(transaction => [
                formatDate(transaction.transaction_date) || '-',
                transaction.partner_name || '-',
                transaction.transaction_type === 'credit' ? 'Credit' : 'Debit',
                transaction.payment_method?.replace('_', ' ') || '-',
                `$${parseFloat(transaction.amount || 0).toFixed(2)}`,
                `$${parseFloat(transaction.balance_before || 0).toFixed(2)}`,
                `$${parseFloat(transaction.balance_after || 0).toFixed(2)}`,
                transaction.reference_no || '-',
                transaction.notes?.substring(0, 20) || '-',
                transaction.added_by_username || '-'
            ]);

            // Add table using autoTable
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 10,
                head: [[
                    translations.transactionDate || 'Date',
                    translations.partnerName || 'Partner',
                    translations.transactionType || 'Type',
                    translations.paymentMethod || 'Method',
                    translations.amount || 'Amount',
                    translations.balanceBefore || 'Balance Before',
                    translations.balanceAfter || 'Balance After',
                    translations.referenceNo || 'Reference',
                    translations.notes || 'Notes',
                    translations.addedBy || 'Added By'
                ]],
                body: tableData,
                theme: 'striped',
                headStyles: {
                    fillColor: [139, 92, 246],
                    textColor: [255, 255, 255],
                    fontSize: 7,
                    halign: 'center'
                },
                bodyStyles: {
                    fontSize: 6
                },
                columnStyles: {
                    0: { cellWidth: 18 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 15, halign: 'center' },
                    3: { cellWidth: 18, halign: 'center' },
                    4: { cellWidth: 18, halign: 'right' },
                    5: { cellWidth: 18, halign: 'right' },
                    6: { cellWidth: 18, halign: 'right' },
                    7: { cellWidth: 20 },
                    8: { cellWidth: 25 },
                    9: { cellWidth: 20 }
                },
                margin: { left: 5, right: 5 },
                didDrawPage: (data) => {
                    doc.setFontSize(7);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `${translations.generatedBy || 'Generated by'}: OurZest POS - Page ${doc.getCurrentPageInfo().pageNumber}`,
                        data.settings.margin.left,
                        doc.internal.pageSize.height - 5
                    );
                }
            });

            // Save the PDF
            const filename = `partner_transactions_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(filename);

            showToast('success', translations.pdfGenerated || 'PDF generated successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleView = (transaction) => {
        setSelectedTransaction(transaction);
        setShowViewModal(true);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    // Get today's date in YYYY-MM-DD format for date inputs
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Set max date for end date based on start date
    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    // Set max date for start date to today
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faHistory} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.partnerTransactions || 'Partner Transactions'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allTransactions || 'All Transactions'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Summary Cards */}
            {currentShop && filteredTransactions.length > 0 && (
                <div className="summary-cards-container" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                    margin: '20px 0'
                }}>
                    <div className="summary-card" style={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        color: 'white',
                        padding: '15px',
                        borderRadius: '10px'
                    }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.totalTransactions || 'Total Transactions'}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totalTransactions}</div>
                    </div>
                    <div className="summary-card" style={{
                        background: '#10b98120',
                        border: '1px solid #10b98140',
                        color: '#10b981',
                        padding: '15px',
                        borderRadius: '10px'
                    }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.totalCredits || 'Total Credits'}</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>${totalCredits.toFixed(2)}</div>
                    </div>
                    <div className="summary-card" style={{
                        background: '#ef444420',
                        border: '1px solid #ef444440',
                        color: '#ef4444',
                        padding: '15px',
                        borderRadius: '10px'
                    }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.totalDebits || 'Total Debits'}</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>${totalDebits.toFixed(2)}</div>
                    </div>
                    <div className="summary-card" style={{
                        background: '#f59e0b20',
                        border: '1px solid #f59e0b40',
                        color: '#f59e0b',
                        padding: '15px',
                        borderRadius: '10px'
                    }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>{translations.netBalance || 'Net Balance'}</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>${netBalance.toFixed(2)}</div>
                    </div>
                </div>
            )}

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="partnerName">{translations.partnerNameFilter || 'Partner Name'}</option>
                            <option value="transactionType">{translations.transactionTypeFilter || 'Transaction Type'}</option>
                            <option value="paymentMethod">{translations.paymentMethodFilter || 'Payment Method'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="amount">{translations.amount || 'Amount Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'partnerName' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={partnerNameFilter}
                                onChange={(e) => setPartnerNameFilter(e.target.value)}
                                placeholder={translations.partnerNameFilter || 'Partner name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'transactionType' && (
                        <div className="filter-group compact">
                            <select
                                value={transactionTypeFilter}
                                onChange={(e) => setTransactionTypeFilter(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                {transactionTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'paymentMethod' && (
                        <div className="filter-group compact">
                            <select
                                value={paymentMethodFilter}
                                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                {paymentMethodOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'amount' && (
                        <>
                            <div className="filter-group compact">
                                <input
                                    type="number"
                                    value={minAmount}
                                    onChange={(e) => setMinAmount(e.target.value)}
                                    placeholder={translations.minAmount || 'Min'}
                                    className="filter-input compact"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="filter-group compact">
                                <input
                                    type="number"
                                    value={maxAmount}
                                    onChange={(e) => setMaxAmount(e.target.value)}
                                    placeholder={translations.maxAmount || 'Max'}
                                    className="filter-input compact"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search transactions...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchTransactions}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    {/* PDF Export Button */}
                    <div className="filter-group compact">
                        <button
                            onClick={exportToPDF}
                            className="refresh-btn-modern compact"
                            disabled={pdfLoading || filteredTransactions.length === 0}
                            title={translations.exportToPDF || 'Export to PDF'}
                        >
                            <FontAwesomeIcon icon={faFilePdf} spin={pdfLoading} />
                            <span className="btn-text">
                                {pdfLoading ? (translations.generatingPDF || 'Generating...') : (translations.exportToPDF || 'PDF')}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingTransactions || 'Loading transactions...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>Please select a shop to view partner transactions</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.transactionDate || 'Date'}</th>
                                            <th className="centered-cell">{translations.partnerName || 'Partner'}</th>
                                            <th className="centered-cell">{translations.transactionType || 'Type'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Method'}</th>
                                            <th className="centered-cell">{translations.amount || 'Amount'}</th>
                                            <th className="centered-cell">{translations.balanceBefore || 'Balance Before'}</th>
                                            <th className="centered-cell">{translations.balanceAfter || 'Balance After'}</th>
                                            <th className="centered-cell">{translations.referenceNo || 'Reference'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentTransactions.map((transaction, index) => {
                                            const addedBy = transaction.added_by_username || '-';
                                            const isSelfAdded = addedBy === user?.username;

                                            return (
                                                <tr key={transaction.id}>
                                                    <td className="id-cell centered-cell">
                                                        <span className="cell-text">{startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{formatDate(transaction.transaction_date)}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{transaction.partner_name || '-'}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <TransactionTypeBadge type={transaction.transaction_type} translations={translations} />
                                                    </td>
                                                    <td className="centered-cell">
                                                        <PaymentMethodBadge method={transaction.payment_method} translations={translations} />
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className={`cell-text amount ${transaction.transaction_type === 'credit' ? 'credit-amount' : 'debit-amount'}`}>
                                                            ${parseFloat(transaction.amount || 0).toFixed(2)}
                                                        </span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">${parseFloat(transaction.balance_before || 0).toFixed(2)}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">${parseFloat(transaction.balance_after || 0).toFixed(2)}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text reference-number" title={transaction.reference_no}>
                                                            {transaction.reference_no?.substring(0, 15) || '-'}
                                                            {transaction.reference_no?.length > 15 && '...'}
                                                        </span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`}>
                                                            <span className="cell-text">{addedBy}</span>
                                                        </span>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="actions-content">
                                                            <button
                                                                onClick={() => handleView(transaction)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentTransactions.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faHistory} />
                                        <p>{translations.noTransactionsFound || 'No partner transactions found'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredTransactions.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} {translations.of || 'of'} {filteredTransactions.length} {translations.items || 'items'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedTransaction && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faHistory} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.transactionDetails || 'Transaction Details'}</h3>
                                <span className="cheque-number-badge">#{selectedTransaction.id}</span>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            {/* Transaction Summary */}
                            <div className="view-section">
                                <h4 className="view-section-title">
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                    Transaction Summary
                                </h4>
                                <div className="view-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.transactionId || 'Transaction ID'}:</span>
                                        <span className="view-value">#{selectedTransaction.id}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.transactionDate || 'Date'}:</span>
                                        <span className="view-value">{formatDate(selectedTransaction.transaction_date)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.transactionType || 'Type'}:</span>
                                        <span className="view-value">
                                            <TransactionTypeBadge type={selectedTransaction.transaction_type} translations={translations} />
                                        </span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.paymentMethod || 'Payment Method'}:</span>
                                        <span className="view-value">
                                            <PaymentMethodBadge method={selectedTransaction.payment_method} translations={translations} />
                                        </span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.amount || 'Amount'}:</span>
                                        <span className={`view-value total-amount ${selectedTransaction.transaction_type === 'credit' ? 'credit-amount' : 'debit-amount'}`}>
                                            ${parseFloat(selectedTransaction.amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.balanceBefore || 'Balance Before'}:</span>
                                        <span className="view-value">${parseFloat(selectedTransaction.balance_before || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.balanceAfter || 'Balance After'}:</span>
                                        <span className="view-value">${parseFloat(selectedTransaction.balance_after || 0).toFixed(2)}</span>
                                    </div>
                                    {selectedTransaction.reference_no && (
                                        <div className="view-row">
                                            <span className="view-label">{translations.referenceNo || 'Reference No'}:</span>
                                            <span className="view-value">{selectedTransaction.reference_no}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Partner Details */}
                            <div className="view-section">
                                <h4 className="view-section-title">
                                    <FontAwesomeIcon icon={faBuilding} />
                                    {translations.partnerDetails || 'Partner Details'}
                                </h4>
                                <div className="view-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.partnerName || 'Partner Name'}:</span>
                                        <span className="view-value">{selectedTransaction.partner_name || '-'}</span>
                                    </div>
                                    {selectedTransaction.partner_id && (
                                        <div className="view-row">
                                            <span className="view-label">Partner ID:</span>
                                            <span className="view-value">{selectedTransaction.partner_id}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedTransaction.notes && (
                                <div className="view-section">
                                    <h4 className="view-section-title">
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                        {translations.notes || 'Notes'}
                                    </h4>
                                    <div className="remarks-box">
                                        {selectedTransaction.notes}
                                    </div>
                                </div>
                            )}

                            {/* Metadata */}
                            <div className="view-section metadata-section">
                                <div className="view-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.addedBy || 'Added By'}:</span>
                                        <span className="view-value">
                                            <span className="added-by-badge">
                                                {selectedTransaction.added_by_username || '-'}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">Created At:</span>
                                        <span className="view-value">{formatDate(selectedTransaction.created_at) || formatDate(selectedTransaction.transaction_date)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartnerTransactions;